import { Component, ElementRef, NgZone } from '@angular/core';
import { HeroMotifBase, RGB } from '../hero-motif/hero-motif.base';

const LOOP = 40;
const W0 = Math.PI * 2 / LOOP;
/** Trees nearer than this (m) sway in the wind; the farther ones are baked still. */
const SWAY_Z = 340;

/** A snowflake falling over the old town, in page pixels; `d` is how near it is (0 far .. 1 near). */
interface Flake {
  x: number;
  y: number;
  d: number;
  phase: number;
  /** Out only while the counter is strong enough for its rank; fading in and out. */
  rank: number;
  shown: number;
}

/** One thing in the town, drawn far to near. */
interface Thing {
  z: number;
  draw: () => void;
}

/**
 * Tallinn's hero board (2026-10-17), after the owner's photographs: the old town from the Patkuli
 * viewpoint on Toompea on a winter afternoon, in true perspective (a camera 40 m above the lower town,
 * every object placed in metres and projected). A blue sky with long level streaks of cloud, the sea; the
 * town's lightly snowed roofs to the harbour, St Olaf's white tower and copper spire, the cathedral's
 * baroque tower; the city wall's round towers under red cones; the park's snowy trees; the long houses
 * with their dormers at our feet. Snow falls; the counter makes it snow harder. The page's wallpaper is snowflakes alone. Sized by the stage's height.
 */
@Component( {
  selector: 'app-hero-tallinn',
  template: '<canvas #canvas class="htal-canvas" aria-hidden="true"></canvas>',
  styles: [ ':host { display: block; position: absolute; inset: 0; overflow: hidden; pointer-events: none; }',
    '.htal-canvas { display: block; position: absolute; inset: 0; }' ],
} )
export class HeroTallinnComponent extends HeroMotifBase {

  private sky: HTMLCanvasElement | null = null;
  private scene: HTMLCanvasElement | null = null;
  private clouds: HTMLCanvasElement | null = null;
  private cloudOff = 0;
  private flakes: Flake[] = [];
  private flakeSprite: HTMLCanvasElement | null = null;
  /** The near trees, swaying in the wind (owner): on screen at their foot, their size, sprite and phase. */
  private nearTrees: { x: number; y: number; px: number; img: HTMLCanvasElement; z: number; ph: number }[] = [];
  /** The big tower at the front, on its own layer so the swaying trees pass before and behind it. */
  private front: { c: HTMLCanvasElement; z: number } | null = null;
  private shadeSprite: HTMLCanvasElement | null = null;
  /** The towers' flags on screen: the top of the pole, the flag's size and colour, a phase. */
  private flags: { x: number; y: number; w: number; h: number; col: RGB; ph: number }[] = [];
  private scaleK = 1;
  // the camera: eye height (m), focal length and horizon (px), the vanishing point's x (px)
  private eye = 40;
  private F = 0;
  private hz = 0;
  private vx = 0;

  constructor( zone: NgZone, host: ElementRef<HTMLElement> ) {
    super( zone, host );
    this.loop = LOOP;
    this.ease = 1.2;
  }

  protected colorsChanged(): void {
    this.scene = null;
    this.flakeSprite = null;
  }

  /** Project a point: x across (metres from the axis), y up, z away from us. */
  private P( x: number, y: number, z: number ): [ number, number ] {
    return [ this.vx + this.F * x / z, this.hz - this.F * ( y - this.eye ) / z ];
  }

  protected build(): void {
    const W = this.width;
    const H = this.height;
    const k = this.scaleK = Math.max( 0.55, Math.min( 1.25, H / 760 ) );
    this.F = 1.8 * H;
    this.hz = 0.43 * H;
    this.vx = W / 2;
    this.scene = null;
    const r = this.seededLocal( 23 );
    this.flakes = [];
    // heavy snow (owner: stronger)
    const n = Math.round( 420 + W * H / 1500 );
    for ( let i = 0; i < n; i++ ) {
      this.flakes.push( { x: r() * W, y: r() * H, d: Math.pow( r(), 1.6 ), phase: r() * Math.PI * 2, rank: r(), shown: 0 } );
    }
    void k;
  }

  protected paint( ctx: CanvasRenderingContext2D, dt: number ): void {
    const W = this.width;
    const H = this.height;
    if ( this.frozen ) {
      // the page's wallpaper: snowflakes alone, still, all down the page
      this.drawWallFlakes( ctx );
      return;
    }
    if ( !this.scene ) { this.bake( ctx.getTransform().a ); }
    if ( !this.scene ) { return; }
    if ( this.sky ) { ctx.drawImage( this.sky, 0, 0, W, H ); }
    if ( this.clouds ) {
      if ( dt > 0 ) { this.cloudOff = ( this.cloudOff + dt * 4 * this.scaleK ) % W; }
      const off = Math.round( this.cloudOff );
      ctx.drawImage( this.clouds, off - W, 0, W, H );
      ctx.drawImage( this.clouds, off, 0, W, H );
    }
    ctx.drawImage( this.scene, 0, 0, W, H );
    this.drawNear( ctx );
    this.drawFlags( ctx );
    this.drawSnow( ctx, dt );
  }

  /**
   * The near trees in the wind (owner), far to near with the big tower in its place among them: each
   * crown bends from its foot, the wind in slow gusts, stronger while the counter counts.
   */
  private drawNear( ctx: CanvasRenderingContext2D ): void {
    const t = this.time * W0;
    const gust = 0.6 + 0.4 * Math.sin( t * 2 ) + 0.5 * this.energy;
    let front = this.front;
    const m = ctx.getTransform();
    for ( const n of this.nearTrees ) {
      if ( front && n.z < front.z ) { ctx.setTransform( m ); ctx.drawImage( front.c, 0, 0, this.width, this.height ); front = null; }
      const k = 0.035 * gust * Math.sin( t * 5 + n.ph ) + 0.012 * Math.sin( t * 11 + n.ph * 2.3 );
      ctx.setTransform( m );
      if ( this.shadeSprite ) { ctx.drawImage( this.shadeSprite, n.x - n.px * 0.52, n.y - n.px * 1.1, n.px * 1.04, n.px * 1.04 ); }
      ctx.translate( n.x, n.y );
      ctx.transform( 1, 0, -k, 1, 0, 0 );
      ctx.drawImage( n.img, -n.px / 2, -n.px, n.px, n.px );
    }
    ctx.setTransform( m );
    if ( front ) { ctx.drawImage( front.c, 0, 0, this.width, this.height ); }
  }

  /** The flags on the towers, rippling out to the right with the wind; livelier on the charge. */
  private drawFlags( ctx: CanvasRenderingContext2D ): void {
    const t = this.time * W0;
    const amp = 0.22 + 0.18 * this.energy;
    for ( const f of this.flags ) {
      ctx.fillStyle = this.rgba( f.col, 1 );
      ctx.beginPath();
      const steps = 6;
      const wave = ( u: number ) => Math.sin( t * 12 + f.ph - u * 4 ) * f.h * amp * u;
      ctx.moveTo( f.x, f.y );
      for ( let i = 1; i <= steps; i++ ) { const u = i / steps; ctx.lineTo( f.x + f.w * u, f.y + wave( u ) ); }
      for ( let i = steps; i >= 0; i-- ) { const u = i / steps; ctx.lineTo( f.x + f.w * u, f.y + f.h * ( 1 - 0.2 * u ) + wave( u ) ); }
      ctx.closePath();
      ctx.fill();
    }
  }

  /** The snow: soft flakes drifting down, the near ones bigger and quicker; more of them on the charge. */
  private drawSnow( ctx: CanvasRenderingContext2D, dt: number ): void {
    const W = this.width;
    const H = this.height;
    const k = this.scaleK;
    const e = this.energy;
    if ( !this.flakeSprite ) {
      const [ ice ] = this.five();
      const c = document.createElement( 'canvas' );
      c.width = c.height = 32;
      const g = c.getContext( '2d' )!;
      const col = this.mix( ice, [ 255, 255, 255 ], 0.75 );
      const gr = g.createRadialGradient( 16, 16, 0, 16, 16, 16 );
      gr.addColorStop( 0, this.rgba( col, 1 ) );
      gr.addColorStop( 0.35, this.rgba( col, 0.8 ) );
      gr.addColorStop( 1, this.rgba( col, 0 ) );
      g.fillStyle = gr;
      g.fillRect( 0, 0, 32, 32 );
      this.flakeSprite = c;
    }
    const share = 0.5 + 0.5 * e;
    const t = performance.now() / 1000;
    const pace = 0.75 + 0.7 * e;
    for ( const f of this.flakes ) {
      const want = f.rank < share ? 1 : 0;
      f.shown = dt > 0 ? f.shown + ( want - f.shown ) * Math.min( 1, dt * 1.5 ) : want;
      if ( dt > 0 ) {
        const fall = ( 14 + 62 * f.d ) * k * pace;
        f.y += fall * dt;
        // carried a little to the right as it falls (owner), swaying
        f.x += ( fall * 0.4 + Math.sin( t * 0.7 + f.phase ) * 10 * ( 0.4 + f.d ) * k ) * dt;
        if ( f.y > H + 6 ) { f.y = -6; f.x = ( ( f.x + f.phase * 997 ) % W + W ) % W; }
        if ( f.x > W + 6 ) { f.x -= W + 12; }
      }
      if ( f.shown < 0.02 ) { continue; }
      const s = ( 1.6 + 5.2 * f.d ) * k;
      ctx.globalAlpha = f.shown * ( 0.45 + 0.5 * f.d );
      ctx.drawImage( this.flakeSprite, f.x - s, f.y - s, s * 2, s * 2 );
    }
    ctx.globalAlpha = 1;
  }

  /** The wallpaper: six-armed snowflakes scattered still down the page, in the theme's ice blue. */
  private drawWallFlakes( ctx: CanvasRenderingContext2D ): void {
    const W = this.width;
    const H = this.height;
    const k = this.scaleK;
    const [ ice, blue ] = this.five();
    const r = this.seededLocal( 5 );
    const n = Math.max( 6, Math.round( W * H / 60000 ) );
    ctx.lineCap = 'round';
    for ( let i = 0; i < n; i++ ) {
      const x = r() * W, y = r() * H, s = ( 7 + r() * 9 ) * k, rot = r() * Math.PI / 3;
      ctx.save();
      ctx.translate( x, y );
      ctx.rotate( rot );
      ctx.strokeStyle = this.rgba( this.mix( ice, blue, 0.35 + r() * 0.2 ), 0.45 + r() * 0.35 );
      ctx.lineWidth = Math.max( 1, s * 0.12 );
      for ( let a = 0; a < 6; a++ ) {
        ctx.save();
        ctx.rotate( a * Math.PI / 3 );
        ctx.beginPath();
        ctx.moveTo( 0, 0 ); ctx.lineTo( 0, -s );
        ctx.moveTo( 0, -s * 0.55 ); ctx.lineTo( -s * 0.26, -s * 0.78 );
        ctx.moveTo( 0, -s * 0.55 ); ctx.lineTo( s * 0.26, -s * 0.78 );
        ctx.moveTo( 0, -s * 0.28 ); ctx.lineTo( -s * 0.16, -s * 0.42 );
        ctx.moveTo( 0, -s * 0.28 ); ctx.lineTo( s * 0.16, -s * 0.42 );
        ctx.stroke();
        ctx.restore();
      }
      ctx.restore();
    }
  }

  /**
   * The still parts, painted once, far to near, in metres projected (owner's second photograph, from the
   * Patkuli viewpoint on a winter afternoon): a blue sky with long level streaks of cloud, the sea and its
   * far shore, the town lightly snowed to the harbour, St Olaf's white tower and green copper spire with the
   * cathedral's dark baroque tower before it, the city wall's round towers under red tiled cones, the snowy
   * trees of the park, the long houses with their dormers at our feet. The low sun from the left.
   */
  private bake( scale: number ): void {
    const W = this.width;
    const H = this.height;
    const hz = this.hz;
    const F = this.F;
    const [ ice, blue, indigo, red ] = this.five();
    const white: RGB = [ 255, 255, 255 ];
    const canvas = () => {
      const c = document.createElement( 'canvas' );
      c.width = Math.ceil( W * scale );
      c.height = Math.ceil( H * scale );
      const g2 = c.getContext( '2d' )!;
      g2.scale( scale, scale );
      return { c, g: g2 };
    };
    const P = ( x: number, y: number, z: number ) => this.P( x, y, z );
    let g: CanvasRenderingContext2D;
    const poly = ( pts: [ number, number ][], fill: string | CanvasGradient ) => {
      g.fillStyle = fill;
      g.beginPath();
      pts.forEach( ( [ x, y ], i ) => i ? g.lineTo( x, y ) : g.moveTo( x, y ) );
      g.closePath();
      g.fill();
    };
    // a winter afternoon, from the theme's ice, blues and brick red
    // a tone nearer the theme's blue (owner)
    const skyTop = this.mix( this.mix( blue, indigo, 0.3 ), [ 40, 70, 120 ], 0.12 );
    const skyMid = this.mix( blue, ice, 0.42 );
    const skyLow = this.mix( this.mix( ice, blue, 0.12 ), [ 250, 236, 214 ], 0.18 );
    const air = this.mix( skyLow, ice, 0.3 );
    const sun: RGB = [ 255, 226, 180 ];
    const snowLit = this.mix( this.mix( ice, white, 0.7 ), sun, 0.12 );
    const snowShade = this.mix( this.mix( ice, indigo, 0.22 ), [ 150, 160, 180 ], 0.3 );
    const tile = this.mix( red, [ 150, 60, 44 ], 0.4 );
    const slate: RGB = [ 74, 78, 90 ];
    const winLit: RGB = [ 255, 214, 150 ];
    const winDark: RGB = [ 60, 64, 82 ];
    const inAir = ( col: RGB, z: number, strength = 0.85 ) => this.mix( col, air, ( 1 - Math.exp( -z / 1900 ) ) * strength );
    const ground = ( x0: number, x1: number, z0: number, z1: number, fill: string | CanvasGradient ) => poly( [ P( x0, 0, z0 ), P( x1, 0, z0 ), P( x1, 0, z1 ), P( x0, 0, z1 ) ], fill );
    // the low sun from the left: a face turned to it (its normal's x below 0) warms, one turned away cools
    const litFace = ( col: RGB, toSun: number, z: number ) => inAir( toSun > 0 ? this.mix( this.mix( col, sun, 0.18 * toSun ), white, 0.08 * toSun ) : this.mix( col, indigo, -0.3 * toSun ), z );

    // ---- the sky: blue, paling to the horizon, the long-exposure streaks of cloud ----
    const sk = canvas();
    g = sk.g;
    {
      const gr = g.createLinearGradient( 0, 0, 0, hz );
      gr.addColorStop( 0, this.rgba( skyTop, 1 ) );
      gr.addColorStop( 0.5, this.rgba( skyMid, 1 ) );
      gr.addColorStop( 1, this.rgba( skyLow, 1 ) );
      g.fillStyle = gr;
      g.fillRect( 0, 0, W, hz + 4 );
    }
    this.sky = sk.c;
    const cl = canvas();
    g = cl.g;
    {
      const cr = this.seededLocal( 77 );
      for ( let i = 0; i < 46; i++ ) {
        const cy = hz * ( 0.05 + 0.88 * Math.pow( cr(), 0.8 ) );
        const near = 1 - cy / hz;
        const cx = cr() * W, rx = H * ( 0.25 + cr() * 0.4 ) * ( 0.6 + near ), ry = H * ( 0.012 + cr() * 0.02 ) * ( 0.5 + near * 1.4 );
        const dark = cr() < 0.6;
        const col = dark ? this.mix( this.mix( skyTop, [ 90, 100, 120 ], 0.5 ), skyLow, 0.3 * ( 1 - near ) ) : this.mix( skyLow, white, 0.4 );
        for ( const dx of [ -W, 0, W ] ) {
          g.save();
          g.translate( cx + dx, cy );
          g.scale( 1, ry / rx );
          const gr = g.createRadialGradient( 0, 0, 0, 0, 0, rx );
          gr.addColorStop( 0, this.rgba( col, dark ? 0.3 : 0.22 ) );
          gr.addColorStop( 0.55, this.rgba( col, dark ? 0.16 : 0.1 ) );
          gr.addColorStop( 1, this.rgba( col, 0 ) );
          g.fillStyle = gr;
          g.beginPath(); g.arc( 0, 0, rx, 0, Math.PI * 2 ); g.fill();
          g.restore();
        }
      }
    }
    this.clouds = cl.c;

    // ---- the town ----
    const sc = canvas();
    g = sc.g;
    // the sea, its far shore low on the left
    ground( -90000, 90000, 2600, 120000, this.rgba( this.mix( this.mix( blue, indigo, 0.4 ), air, 0.35 ), 1 ) );
    {
      const sea = g.createLinearGradient( 0, hz, 0, P( 0, 0, 2600 )[ 1 ] );
      sea.addColorStop( 0, this.rgba( air, 0.55 ) );
      sea.addColorStop( 1, this.rgba( air, 0 ) );
      ground( -90000, 90000, 2600, 120000, sea );
      const shore = this.mix( this.mix( [ 70, 80, 96 ], air, 0.55 ), indigo, 0.1 );
      g.fillStyle = this.rgba( shore, 1 );
      g.beginPath();
      const [ , y0 ] = P( 0, 0, 14000 );
      g.moveTo( 0, y0 );
      for ( let x = 0; x <= W; x += 8 ) {
        const u = x / W;
        const hgt = ( 1.6 + Math.sin( u * 9 ) * 0.5 + Math.sin( u * 23 ) * 0.3 ) * H / 900 * ( u < 0.55 ? 1 : 0.5 );
        g.lineTo( x, y0 - hgt );
      }
      g.lineTo( W, y0 + 1 ); g.lineTo( 0, y0 + 1 ); g.closePath(); g.fill();
    }
    // the ground of the lower town, snow lying thin over it
    {
      const zg = [ 2600, 1300, 700, 400, 240, 120 ];
      for ( let i = 0; i < zg.length - 1; i++ ) {
        ground( -90000, 90000, zg[ i + 1 ], zg[ i ], this.rgba( inAir( this.mix( snowShade, [ 120, 118, 124 ], 0.35 ), ( zg[ i ] + zg[ i + 1 ] ) / 2 ), 1 ) );
      }
    }

    const things: Thing[] = [];
    this.nearTrees = [];
    this.front = null;
    this.flags = [];
    // the shade behind each near crown, painted once, drawn with the crown in the moving pass so each
    // one darkens the crowns behind it as before
    {
      const c = document.createElement( 'canvas' );
      c.width = c.height = 128;
      const sg = c.getContext( '2d' )!;
      const gr = sg.createRadialGradient( 64, 64, 0, 64, 64, 64 );
      gr.addColorStop( 0, this.rgba( inAir( this.mix( snowShade, indigo, 0.2 ), 250 ), 0.4 ) );
      gr.addColorStop( 1, this.rgba( inAir( snowShade, 250 ), 0 ) );
      sg.fillStyle = gr;
      sg.fillRect( 0, 0, 128, 128 );
      this.shadeSprite = c;
    }
    const add = ( z: number, draw: () => void ) => things.push( { z, draw } );

    // a roof's face dusted with snow: its tiles or slates under a thin uneven white
    const dusted = ( pts: [ number, number ][], base: RGB, z: number, toSun: number, seed: number, cover = 0.62 ) => {
      const rb = litFace( base, toSun, z );
      const snow = toSun > 0 ? inAir( snowLit, z ) : inAir( snowShade, z );
      const ys = pts.map( p => p[ 1 ] );
      const gr = g.createLinearGradient( 0, Math.min( ...ys ), 0, Math.max( ...ys ) );
      gr.addColorStop( 0, this.rgba( this.mix( rb, snow, cover * 0.85 ), 1 ) );
      gr.addColorStop( 1, this.rgba( this.mix( rb, snow, cover ), 1 ) );
      poly( pts, gr );
      const s = F / z;
      if ( s < 3 ) { return; }
      g.save();
      g.beginPath(); pts.forEach( ( [ px, py ], i ) => i ? g.lineTo( px, py ) : g.moveTo( px, py ) ); g.closePath(); g.clip();
      const xs = pts.map( p => p[ 0 ] );
      const x0 = Math.min( ...xs ), x1 = Math.max( ...xs ), y0 = Math.min( ...ys ), y1 = Math.max( ...ys );
      const rr = this.seededLocal( seed );
      const n = Math.min( 260, ( x1 - x0 ) * ( y1 - y0 ) / ( s * s * 1.2 ) );
      for ( let q = 0; q < n; q++ ) {
        const px = x0 + rr() * ( x1 - x0 ), py = y0 + rr() * ( y1 - y0 );
        const bare = rr() < 0.45;
        const col = bare ? this.mix( rb, [ 30, 30, 40 ], 0.1 ) : this.mix( snow, white, 0.2 );
        const rad = s * ( 0.4 + rr() * 0.9 );
        const bg = g.createRadialGradient( px, py, 0, px, py, rad );
        bg.addColorStop( 0, this.rgba( col, bare ? 0.16 : 0.2 ) );
        bg.addColorStop( 1, this.rgba( col, 0 ) );
        g.fillStyle = bg;
        g.save(); g.translate( px, py ); g.scale( 2.2, 0.5 ); g.translate( -px, -py );
        g.fillRect( px - rad, py - rad, rad * 2, rad * 2 );
        g.restore();
      }
      g.restore();
    };

    // a house: plastered or stone walls, a dusted roof (its ridge across or toward us), its windows,
    // dormers and chimneys on the nearer ones
    const house = ( x0: number, x1: number, z0: number, d: number, h: number, rh: number, wall: RGB, roof: RGB, ridgeAcross: boolean, lit: number, seed: number, dormers = false ) => {
      const zc = z0 + d / 2;
      const hr = this.seededLocal( seed );
      const s = F / z0;
      // the side wall facing the axis: the right houses show their left side, lit by the sun
      if ( x0 > 0 ) { poly( [ P( x0, 0, z0 ), P( x0, 0, z0 + d ), P( x0, h, z0 + d ), P( x0, h, z0 ) ], this.rgba( litFace( wall, 0.9, z0 ), 1 ) ); }
      if ( x1 < 0 ) { poly( [ P( x1, 0, z0 ), P( x1, 0, z0 + d ), P( x1, h, z0 + d ), P( x1, h, z0 ) ], this.rgba( litFace( wall, -0.8, z0 ), 1 ) ); }
      const wg = g.createLinearGradient( 0, P( 0, 0, z0 )[ 1 ], 0, P( 0, h, z0 )[ 1 ] );
      wg.addColorStop( 0, this.rgba( litFace( wall, -0.35, z0 ), 1 ) );
      wg.addColorStop( 1, this.rgba( litFace( wall, 0.1, z0 ), 1 ) );
      poly( [ P( x0, 0, z0 ), P( x1, 0, z0 ), P( x1, h, z0 ), P( x0, h, z0 ) ], wg );
      const xm = ( x0 + x1 ) / 2;
      if ( ridgeAcross ) {
        if ( x0 > 0 ) { poly( [ P( x0, h, z0 ), P( x0, h, z0 + d ), P( x0, h + rh, zc ) ], this.rgba( litFace( wall, 0.9, z0 ), 1 ) ); }
        if ( x1 < 0 ) { poly( [ P( x1, h, z0 ), P( x1, h, z0 + d ), P( x1, h + rh, zc ) ], this.rgba( litFace( wall, -0.8, z0 ), 1 ) ); }
        dusted( [ P( x0 - 0.4, h, z0 - 0.4 ), P( x1 + 0.4, h, z0 - 0.4 ), P( x1 + 0.4, h + rh, zc ), P( x0 - 0.4, h + rh, zc ) ], roof, z0, 0.25, seed );
        // the eaves' dark edge
        const ea = P( x0 - 0.4, h - 0.3, z0 - 0.4 ), eb = P( x1 + 0.4, h - 0.3, z0 - 0.4 ), b = P( x1 + 0.4, h, z0 - 0.4 ), a = P( x0 - 0.4, h, z0 - 0.4 );
        poly( [ ea, eb, b, a ], this.rgba( inAir( this.mix( roof, [ 30, 30, 40 ], 0.4 ), z0 ), 1 ) );
        if ( dormers && s > 3 ) {
          for ( let dx = x0 + 3; dx < x1 - 3; dx += 5.5 + hr() * 2 ) {
            const dz = z0 + d * 0.16, dy = h + rh * 0.32;
            const fa = P( dx, dy, dz ), fb = P( dx + 2.2, dy + 2.2, dz );
            g.fillStyle = this.rgba( litFace( wall, 0.2, z0 ), 1 );
            g.fillRect( fa[ 0 ], fb[ 1 ], fb[ 0 ] - fa[ 0 ], fa[ 1 ] - fb[ 1 ] );
            g.fillStyle = this.rgba( inAir( winDark, z0 ), 1 );
            g.fillRect( fa[ 0 ] + ( fb[ 0 ] - fa[ 0 ] ) * 0.25, fb[ 1 ] + ( fa[ 1 ] - fb[ 1 ] ) * 0.3, ( fb[ 0 ] - fa[ 0 ] ) * 0.5, ( fa[ 1 ] - fb[ 1 ] ) * 0.55 );
            dusted( [ P( dx - 0.3, dy + 2.2, dz ), P( dx + 2.5, dy + 2.2, dz ), P( dx + 1.1, dy + 3.3, dz + 0.8 ) ], roof, z0, 0.3, seed + dx );
          }
        }
        if ( s > 2.2 ) {
          for ( let ci = 0; ci < ( x1 - x0 > 16 ? 2 : 1 ); ci++ ) {
            const cx = x0 + ( x1 - x0 ) * ( 0.15 + hr() * 0.7 ), cz = z0 + d * ( 0.3 + hr() * 0.15 );
            const yb = h + rh * ( cz - z0 ) / ( d / 2 );
            const c0 = P( cx - 0.7, yb - 0.4, cz ), c1 = P( cx + 0.7, yb + 1.8, cz );
            g.fillStyle = this.rgba( litFace( [ 206, 200, 194 ], 0.3, cz ), 1 );
            g.fillRect( c0[ 0 ], c1[ 1 ], c1[ 0 ] - c0[ 0 ], c0[ 1 ] - c1[ 1 ] );
            g.fillStyle = this.rgba( litFace( [ 150, 146, 150 ], -0.6, cz ), 1 );
            g.fillRect( c0[ 0 ] + ( c1[ 0 ] - c0[ 0 ] ) * 0.62, c1[ 1 ], ( c1[ 0 ] - c0[ 0 ] ) * 0.38, c0[ 1 ] - c1[ 1 ] );
          }
        }
      } else {
        // the ridge toward us: both slopes from above, the gable facing us
        const top0 = P( xm, h + rh, z0 ), top1 = P( xm, h + rh, z0 + d );
        const l0 = P( x0 - 0.4, h, z0 ), l1 = P( x0 - 0.4, h, z0 + d ), r0 = P( x1 + 0.4, h, z0 ), r1 = P( x1 + 0.4, h, z0 + d );
        dusted( [ r0, r1, top1, top0 ], roof, z0, -0.6, seed + 1 );
        dusted( [ l0, l1, top1, top0 ], roof, z0, 0.8, seed + 2 );
        poly( [ P( x0, h, z0 ), P( x1, h, z0 ), P( xm, h + rh, z0 ) ], this.rgba( litFace( wall, 0, z0 ), 1 ) );
      }
      // the windows, row by row
      const floors = Math.max( 1, Math.floor( ( h - 1 ) / 3.3 ) );
      const cols = Math.max( 1, Math.floor( ( x1 - x0 ) / 3.2 ) );
      for ( let f = 0; f < floors; f++ ) {
        for ( let cI = 0; cI < cols; cI++ ) {
          const wx = x0 + ( x1 - x0 ) * ( cI + 0.5 ) / cols, wy = 1.5 + f * 3.3;
          const on = hr() < lit;
          if ( s * 1.1 < 1.3 ) {
            if ( hr() < 0.5 ) { continue; }
            const [ px, py ] = P( wx, wy + 0.8, z0 );
            g.fillStyle = this.rgba( inAir( on ? winLit : winDark, z0, 0.6 ), 0.7 );
            g.fillRect( px - 0.5, py - 0.6, 1, 1.2 );
            continue;
          }
          const a = P( wx - 0.5, wy, z0 ), b = P( wx + 0.5, wy + 1.6, z0 );
          g.fillStyle = this.rgba( inAir( on ? winLit : this.mix( wall, winDark, 0.6 ), z0, 0.5 ), 1 );
          g.fillRect( a[ 0 ], b[ 1 ], b[ 0 ] - a[ 0 ], a[ 1 ] - b[ 1 ] );
        }
      }
    };

    // a round tower of the city wall: limestone, slit windows, a red tiled cone and its vane, the sun on its left
    const tower = ( x: number, z: number, rad: number, h: number, ch: number ) => {
      const [ lx ] = P( x - rad, 0, z ), [ rx ] = P( x + rad, 0, z );
      const [ , top ] = P( x, h, z ), [ , bot ] = P( x, 0, z );
      const ey = Math.max( 0.5, ( P( x, h, z - rad )[ 1 ] - P( x, h, z + rad )[ 1 ] ) / 2 );
      const eyB = Math.max( 0.5, ( P( x, 0, z - rad )[ 1 ] - P( x, 0, z + rad )[ 1 ] ) / 2 );
      const cxp = ( lx + rx ) / 2, rw = ( rx - lx ) / 2;
      const stone: RGB = [ 168, 164, 156 ];
      const body = g.createLinearGradient( lx, 0, rx, 0 );
      body.addColorStop( 0, this.rgba( litFace( stone, 0.5, z ), 1 ) );
      body.addColorStop( 0.3, this.rgba( litFace( stone, 1, z ), 1 ) );
      body.addColorStop( 0.65, this.rgba( litFace( stone, -0.2, z ), 1 ) );
      body.addColorStop( 1, this.rgba( litFace( stone, -1, z ), 1 ) );
      g.fillStyle = body;
      g.beginPath();
      g.moveTo( lx, top );
      g.lineTo( lx, bot );
      g.ellipse( cxp, bot, rw, eyB, 0, Math.PI, 0, true );
      g.lineTo( rx, top );
      g.closePath();
      g.fill();
      // the limestone's courses: soft mottling inside the body
      g.save();
      g.clip();
      const tr = this.seededLocal( Math.floor( x * 7 + z ) );
      const ps = F / z;
      for ( let i = 0; i < Math.min( 700, rw * ( bot - top ) / 5 ); i++ ) {
        const sx = lx + tr() * rw * 2, sy = top + tr() * ( bot - top );
        g.fillStyle = this.rgba( tr() < 0.5 ? this.mix( stone, [ 70, 66, 70 ], 0.35 ) : this.mix( stone, white, 0.2 ), 0.22 );
        g.fillRect( sx, sy, Math.max( 1, ps * 1.1 ), Math.max( 0.6, ps * 0.4 ) );
      }
      g.restore();
      for ( const [ fx, fy ] of [ [ -0.45, 0.78 ], [ 0.2, 0.62 ], [ -0.1, 0.4 ], [ 0.45, 0.8 ] ] ) {
        const [ wx, wy ] = P( x + fx * rad, h * fy, z - rad * 0.95 );
        g.fillStyle = this.rgba( inAir( [ 40, 40, 52 ], z ), 1 );
        g.fillRect( wx - ps * 0.35, wy - ps * 1.1, ps * 0.7, ps * 2.2 );
      }
      // the cone: red tiles, sunlit on the left, a dusting of snow, a small dark eave line
      const ov = rad * 1.12;
      const [ clx ] = P( x - ov, h, z ), [ crx ] = P( x + ov, h, z );
      const ap = P( x, h + ch, z );
      const cone = g.createLinearGradient( clx, 0, crx, 0 );
      cone.addColorStop( 0, this.rgba( litFace( tile, 0.6, z ), 1 ) );
      cone.addColorStop( 0.3, this.rgba( litFace( tile, 1, z ), 1 ) );
      cone.addColorStop( 0.7, this.rgba( litFace( tile, -0.3, z ), 1 ) );
      cone.addColorStop( 1, this.rgba( litFace( tile, -1, z ), 1 ) );
      g.fillStyle = cone;
      g.beginPath();
      g.moveTo( clx, top );
      g.lineTo( ap[ 0 ], ap[ 1 ] );
      g.lineTo( crx, top );
      g.ellipse( cxp, top, ( crx - clx ) / 2, ey * 1.12, 0, 0, Math.PI, false );
      g.closePath();
      g.fill();
      // the tiles: courses of soft light and shade round the cone, a few flecks of snow
      g.save();
      g.clip();
      for ( let q = 1; q < 14; q++ ) {
        const f = q / 14;
        const yy = ap[ 1 ] + ( top - ap[ 1 ] ) * f;
        const half = ( crx - clx ) / 2 * f;
        g.strokeStyle = this.rgba( this.mix( tile, [ 40, 20, 20 ], 0.5 ), 0.16 );
        g.lineWidth = Math.max( 0.5, ps * 0.25 );
        g.beginPath(); g.ellipse( ap[ 0 ], yy, half, ey * 1.12 * f, 0, 0, Math.PI, false ); g.stroke();
      }
      for ( let q = 0; q < 40; q++ ) {
        const f = Math.sqrt( tr() );
        const yy = ap[ 1 ] + ( top - ap[ 1 ] ) * f + ey * f * tr();
        const xx = ap[ 0 ] + ( tr() - 0.5 ) * ( crx - clx ) * f;
        g.fillStyle = this.rgba( inAir( snowLit, z ), 0.35 );
        g.fillRect( xx, yy, Math.max( 0.8, ps * 0.7 ), Math.max( 0.5, ps * 0.25 ) );
      }
      g.restore();
      g.strokeStyle = this.rgba( inAir( [ 50, 52, 62 ], z ), 1 );
      g.lineWidth = Math.max( 0.6, ps * 0.2 );
      const vt = P( x, h + ch + 4, z );
      g.beginPath(); g.moveTo( ap[ 0 ], ap[ 1 ] ); g.lineTo( vt[ 0 ], vt[ 1 ] ); g.stroke();
      // its flag, waving in the wind on the moving layer (owner)
      this.flags.push( { x: vt[ 0 ], y: vt[ 1 ] + ps * 0.3, w: ps * 1.5, h: ps * 0.7, col: inAir( [ 50, 52, 62 ], z ), ph: x * 0.13 } );
    };

    // a tree laden with snow (owner: not bare): dark branches, the snow lying in soft clusters along them,
    // lit by the low sun from the left; painted once per shape and distance, then placed
    const sprites = new Map<string, HTMLCanvasElement>();
    const snowTree = ( shape: number, hazeLevel: number ) => {
      const key = shape + ':' + hazeLevel;
      const hit = sprites.get( key );
      if ( hit ) { return hit; }
      const S = 360;
      const cv = document.createElement( 'canvas' );
      cv.width = S; cv.height = S;
      const tg = cv.getContext( '2d' )!;
      const zr = [ 180, 450, 1100 ][ hazeLevel ];
      const fLit = inAir( this.mix( snowLit, white, 0.3 ), zr );
      const fShade = inAir( snowShade, zr );
      const bark = inAir( [ 62, 54, 54 ], zr );
      const rr = this.seededLocal( 400 + shape * 31 );
      const tips: [ number, number, number ][] = [];
      const branch = ( x: number, y: number, ang: number, len: number, w: number, depth: number ) => {
        const ex = x + Math.cos( ang ) * len, ey = y + Math.sin( ang ) * len;
        tg.strokeStyle = this.rgba( bark, 1 );
        tg.lineWidth = Math.max( 0.5, w );
        tg.lineCap = 'round';
        tg.beginPath(); tg.moveTo( x, y ); tg.quadraticCurveTo( ( x + ex ) / 2 + ( rr() - 0.5 ) * len * 0.2, ( y + ey ) / 2, ex, ey ); tg.stroke();
        if ( depth >= 2 ) { tips.push( [ ex, ey, depth ] ); }
        if ( depth >= 7 ) { return; }
        const n = depth < 2 ? 2 : 3;
        for ( let i = 0; i < n; i++ ) {
          const na = ang + ( i - ( n - 1 ) / 2 ) * ( 0.5 + rr() * 0.3 ) + ( rr() - 0.5 ) * 0.3;
          const up = na + ( -Math.PI / 2 - na ) * 0.18;
          branch( ex, ey, up, len * ( 0.66 + rr() * 0.12 ), w * 0.66, depth + 1 );
        }
      };
      branch( S / 2, S, -Math.PI / 2 + ( rr() - 0.5 ) * 0.1, S * 0.22, S * 0.022, 0 );
      // the snow: soft clusters round the twigs, white where the sun reaches (upper left), blue grey beneath
      for ( const [ tx, ty, depth ] of tips ) {
        const n = depth >= 4 ? 2 : 1;
        for ( let i = 0; i < n; i++ ) {
          const px = tx + ( rr() - 0.5 ) * 14, py = ty + ( rr() - 0.5 ) * 10 - 2;
          const rad = ( 4 + rr() * 7 ) * ( depth >= 5 ? 1 : 0.85 );
          const lightK = Math.min( 1, Math.max( 0, 0.3 + ( 1 - py / S ) * 0.6 + ( 0.5 - px / S ) * 0.5 + ( rr() - 0.5 ) * 0.2 ) );
          const col = this.mix( fShade, fLit, lightK );
          const gr = tg.createRadialGradient( px - rad * 0.3, py - rad * 0.35, 0, px, py, rad );
          gr.addColorStop( 0, this.rgba( this.mix( col, white, 0.25 ), 0.95 ) );
          gr.addColorStop( 0.7, this.rgba( col, 0.85 ) );
          gr.addColorStop( 1, this.rgba( col, 0 ) );
          tg.fillStyle = gr;
          tg.beginPath(); tg.arc( px, py, rad, 0, Math.PI * 2 ); tg.fill();
        }
      }
      // the finest twigs showing through the snow, faintly
      tg.globalAlpha = 0.22;
      tg.strokeStyle = this.rgba( bark, 1 );
      tg.lineWidth = 0.8;
      for ( const [ tx, ty ] of tips.filter( ( _, i ) => i % 3 === 0 ) ) {
        tg.beginPath(); tg.moveTo( tx, ty ); tg.lineTo( tx + ( rr() - 0.5 ) * 16, ty - 6 - rr() * 10 ); tg.stroke();
      }
      tg.globalAlpha = 1;
      sprites.set( key, cv );
      return cv;
    };
    const tree = ( x: number, z: number, h: number, shape: number ) => {
      const [ bx, by ] = P( x, 0, z );
      const px = F * h / z;
      const lvl = z < 280 ? 0 : z < 700 ? 1 : 2;
      if ( px < 4 ) {
        const c = inAir( this.mix( snowLit, snowShade, 0.4 ), z );
        const gr = g.createRadialGradient( bx, by - px * 0.6, 0, bx, by - px * 0.6, px * 0.5 );
        gr.addColorStop( 0, this.rgba( c, 0.55 ) );
        gr.addColorStop( 1, this.rgba( c, 0 ) );
        g.fillStyle = gr;
        g.fillRect( bx - px * 0.5, by - px * 1.1, px, px );
        return;
      }
      const img = snowTree( shape % 6, lvl );
      if ( z < SWAY_Z ) { this.nearTrees.push( { x: bx, y: by, px, img, z, ph: ( x * 0.37 + z * 0.11 ) % ( Math.PI * 2 ) } ); return; }
      // a soft shade behind the crown, so neighbouring crowns part
      const sh = g.createRadialGradient( bx, by - px * 0.58, 0, bx, by - px * 0.58, px * 0.52 );
      sh.addColorStop( 0, this.rgba( inAir( this.mix( snowShade, indigo, 0.2 ), z ), 0.4 ) );
      sh.addColorStop( 1, this.rgba( inAir( snowShade, z ), 0 ) );
      g.fillStyle = sh;
      g.fillRect( bx - px * 0.52, by - px * 1.1, px * 1.04, px * 1.04 );
      g.drawImage( img, bx - px / 2, by - px, px, px );
    };

    // ---- what stands where (metres; x across, z away) ----
    const halfAt = ( z: number ) => ( W / 2 + 40 ) * z / F;
    const clear: [ number, number, number, number ][] = [];
    const isClear = ( x0: number, x1: number, z0: number, z1: number ) => !clear.some( ( [ a, b, c, d ] ) => x1 > a && x0 < b && z1 > c && z0 < d );
    // the parks of snowy trees on both sides (owner: the right a copy of the left, no houses there), the
    // old town only in the middle, toward St Olaf's
    const park = ( x: number, z: number ) => z > 330 && z < 1700 && Math.abs( x ) > 36 + ( z - 330 ) * 0.04;
    // a little to the right (owner)
    // and 110 px further right (owner: 50, then 60)
    const olaf = { x: 132 + 110 * 740 / F, z: 740 };
    clear.push( [ olaf.x - 12, olaf.x + 12, olaf.z - 10, olaf.z + 70 ] );
    const dome = { x: 90 + 60 * 570 / F, z: 570 };
    clear.push( [ dome.x - 9, dome.x + 9, dome.z - 8, dome.z + 34 ] );
    const towers: [ number, number, number, number, number ][] = [
      // the big one at the front left (owner)
      [ -42 - 50 * 214 / F, 214, 8, 30, 19 ], [ -150, 520, 7, 26, 15 ], [ -128, 548, 6, 26, 17 ], [ -64, 400, 4.6, 15, 6 ],
    ];
    // the left side's towers copied mirrored to the right (owner), all but the big one in the middle
    // (those that would stand in St Olaf's way are left out)
    // the town ends at St Olaf's (owner): past it to the right only trees and the wall's towers
    const pastOlaf = ( x: number, z: number ) => x / z > olaf.x / olaf.z;
    for ( const t of towers.slice( 1 ) ) {
      if ( Math.abs( -t[ 0 ] - olaf.x ) < 24 && Math.abs( t[ 1 ] - olaf.z ) < 150 ) { continue; }
      towers.push( [ -t[ 0 ], t[ 1 ], t[ 2 ], t[ 3 ], t[ 4 ] ] );
    }
    for ( const [ x, z, rad ] of towers ) { clear.push( [ x - rad - 2, x + rad + 2, z - rad - 2, z + rad + 4 ] ); }

    const walls: RGB[] = [ [ 226, 220, 204 ], [ 236, 222, 182 ], [ 204, 210, 216 ], [ 180, 178, 176 ], [ 232, 210, 186 ], [ 214, 220, 206 ] ];
    const roofs: RGB[] = [ tile, tile, slate, this.mix( tile, slate, 0.5 ), slate ];
    // the far town to the harbour
    for ( let row = 0, z = 2500; z > 420; row++, z *= 0.955 ) {
      const half = halfAt( z );
      let x = -half;
      const rr = this.seededLocal( 900 + row );
      while ( x < half ) {
        const w = 10 + rr() * 12, d = 9 + rr() * 6, h = 7 + rr() * 9;
        const x0 = x, x1 = x + w, zz = z + rr() * z * 0.03;
        x += w + 1 + rr() * 4;
        if ( park( x0, zz ) || park( x1, zz ) || !isClear( x0, x1, zz, zz + d ) ) { continue; }
        // past St Olaf's and in the middle the town gives way to trees (owner: the middle city gone)
        if ( pastOlaf( x0, zz ) || zz < 1700 ) {
          for ( let tx = x0; tx < x1; tx += 6 ) { const ts = Math.floor( rr() * 6 ), t0 = tx; add( zz, () => tree( t0, zz, 15, ts ) ); }
          continue;
        }
        if ( rr() < 0.08 ) { const tx = x0 + w / 2, ts = Math.floor( rr() * 6 ); add( zz, () => tree( tx, zz, 15, ts ) ); continue; }
        const wall = walls[ Math.floor( rr() * walls.length ) ], roof = roofs[ Math.floor( rr() * roofs.length ) ];
        const across = rr() < 0.65, lit = 0.06, seed = Math.floor( rr() * 1e6 ), rh = 4 + rr() * 4;
        add( zz, () => house( x0, x1, zz, d, h, rh, wall, roof, across, lit, seed ) );
      }
    }
    // the park's snowy trees
    {
      const rr = this.seededLocal( 61 );
      for ( let z = 1700; z > 300; z *= 0.97 ) {
        const half = halfAt( z );
        // both sides (owner: the right like the left, no empty ground)
        for ( let x = -half; x < half; x += 6 + rr() * 7 ) {
          if ( !park( x, z ) ) { continue; }
          const zz = z * ( 1 + ( rr() - 0.5 ) * 0.03 );
          if ( !isClear( x - 3, x + 3, zz, zz + 3 ) ) { continue; }
          const h = 16 + rr() * 9, ts = Math.floor( rr() * 6 ), xx = x;
          add( zz, () => tree( xx, zz, h, ts ) );
        }
      }
    }
    // St Olaf's church, as in the photograph: the long nave behind with a small green spirelet at its end;
    // the white limestone tower with its corner buttresses, string courses and tiers of lancets, the
    // belfry's tall openings; four little copper turrets at its corners; the tall octagonal spire, dark
    // weathered copper turning green at its foot and its edges, a lantern ring partway, the cross on top
    add( olaf.z, () => {
      const { x, z } = olaf;
      const s = F / z;
      const darkTile = this.mix( tile, [ 60, 40, 36 ], 0.35 );
      house( x - 8, x + 8, z + 14, 62, 22, 17, [ 224, 216, 198 ], darkTile, false, 0, 11 );
      const copper = this.mix( [ 96, 146, 124 ], blue, 0.1 ), dark: RGB = [ 52, 50, 46 ];
      // the spirelet at the nave's far end
      {
        const zc = z + 70, y0 = 39;
        const a = P( x - 1.6, y0, zc ), b = P( x + 1.6, y0, zc ), ap = P( x, y0 + 16, zc );
        poly( [ a, [ ap[ 0 ], ap[ 1 ] ], [ ( a[ 0 ] + b[ 0 ] ) / 2, a[ 1 ] ] ], this.rgba( litFace( copper, 0.6, zc ), 1 ) );
        poly( [ [ ( a[ 0 ] + b[ 0 ] ) / 2, a[ 1 ] ], [ ap[ 0 ], ap[ 1 ] ], b ], this.rgba( litFace( copper, -0.6, zc ), 1 ) );
      }
      const tw = 8, th = 58;
      const st: RGB = [ 234, 228, 214 ];
      // the side toward the sun, then the front
      poly( [ P( x - tw, 0, z ), P( x - tw, 0, z + 2 * tw ), P( x - tw, th, z + 2 * tw ), P( x - tw, th, z ) ], this.rgba( litFace( st, 0.95, z ), 1 ) );
      const front = g.createLinearGradient( 0, P( x, 0, z )[ 1 ], 0, P( x, th, z )[ 1 ] );
      front.addColorStop( 0, this.rgba( litFace( st, 0.15, z ), 1 ) );
      front.addColorStop( 1, this.rgba( litFace( st, 0.45, z ), 1 ) );
      poly( [ P( x - tw, 0, z ), P( x + tw, 0, z ), P( x + tw, th, z ), P( x - tw, th, z ) ], front );
      // the corner buttresses, a shade darker on their turned faces, stepping in as they rise
      for ( const side of [ -1, 1 ] ) {
        for ( const [ y0, y1, wdt ] of [ [ 0, 24, 1.8 ], [ 24, 40, 1.4 ], [ 40, th, 1.1 ] ] ) {
          const xo = x + side * tw, xi = xo - side * wdt;
          poly( [ P( xi, y0, z - 0.6 ), P( xo, y0, z - 0.6 ), P( xo, y1, z - 0.6 ), P( xi, y1, z - 0.6 ) ], this.rgba( litFace( st, side < 0 ? 0.7 : -0.25, z ), 1 ) );
        }
      }
      // the string courses, soft shadows under each ledge
      for ( const yc of [ 24, 40, th - 0.6 ] ) {
        const a = P( x - tw, yc, z - 0.7 ), b = P( x + tw, yc - 0.6, z - 0.7 );
        g.fillStyle = this.rgba( litFace( st, 0.6, z ), 1 );
        g.fillRect( a[ 0 ], a[ 1 ], b[ 0 ] - a[ 0 ], Math.max( 0.8, s * 0.5 ) );
        g.fillStyle = this.rgba( litFace( st, -0.8, z ), 0.5 );
        g.fillRect( a[ 0 ], a[ 1 ] + Math.max( 0.8, s * 0.5 ), b[ 0 ] - a[ 0 ], Math.max( 0.6, s * 0.3 ) );
      }
      // the limestone's faint blocks
      {
        const tr = this.seededLocal( 717 );
        g.save();
        g.beginPath(); [ P( x - tw, 0, z ), P( x + tw, 0, z ), P( x + tw, th, z ), P( x - tw, th, z ) ].forEach( ( [ px, py ], i ) => i ? g.lineTo( px, py ) : g.moveTo( px, py ) ); g.closePath(); g.clip();
        const [ ax, ay ] = P( x - tw, th, z ), [ bx, by ] = P( x + tw, 0, z );
        for ( let q = 0; q < 500; q++ ) {
          g.fillStyle = this.rgba( tr() < 0.5 ? this.mix( st, [ 120, 116, 110 ], 0.4 ) : white, 0.12 );
          g.fillRect( ax + tr() * ( bx - ax ), ay + tr() * ( by - ay ), Math.max( 1, s * 1.2 ), Math.max( 0.5, s * 0.45 ) );
        }
        g.restore();
      }
      // the lancets: tall, pointed, in tiers, deep in shadow
      const lancet = ( cx: number, y0: number, hh: number, w: number ) => {
        const [ wx, wy ] = P( cx, y0, z - 0.2 );
        const ww = s * w / 2;
        g.fillStyle = this.rgba( inAir( [ 62, 66, 82 ], z ), 1 );
        g.beginPath();
        g.moveTo( wx - ww, wy );
        g.lineTo( wx - ww, wy - s * hh + ww * 1.6 );
        g.quadraticCurveTo( wx - ww, wy - s * hh + ww * 0.3, wx, wy - s * hh );
        g.quadraticCurveTo( wx + ww, wy - s * hh + ww * 0.3, wx + ww, wy - s * hh + ww * 1.6 );
        g.lineTo( wx + ww, wy );
        g.closePath();
        g.fill();
        g.strokeStyle = this.rgba( litFace( st, 0.7, z ), 0.8 );
        g.lineWidth = Math.max( 0.5, s * 0.25 );
        g.stroke();
      };
      lancet( x, 12, 7, 1.2 );
      for ( const fx of [ -1.6, 1.6 ] ) { lancet( x + fx, 28, 9, 1.1 ); }
      for ( const fx of [ -3.4, -1.15, 1.15, 3.4 ] ) { lancet( x + fx, 43, 12, 1.2 ); }
      // the turrets at the corners: little octagonal copper spires, the back ones first
      const turret = ( tx: number, tz: number ) => {
        const a = P( tx - 1.3, th, tz ), b = P( tx + 1.3, th, tz ), ap = P( tx, th + 10, tz ), m = P( tx, th, tz );
        poly( [ a, [ m[ 0 ], m[ 1 ] ], [ ap[ 0 ], ap[ 1 ] ] ], this.rgba( litFace( copper, 0.7, tz ), 1 ) );
        poly( [ [ m[ 0 ], m[ 1 ] ], b, [ ap[ 0 ], ap[ 1 ] ] ], this.rgba( litFace( copper, -0.5, tz ), 1 ) );
      };
      turret( x - tw + 0.8, z + 2 * tw - 0.8 ); turret( x + tw - 0.8, z + 2 * tw - 0.8 );
      // the spire, an octagon seen across a face: the sunlit diagonal face, the front, the shaded one
      const zc = z + tw, y0 = th + 1, y1 = 123;
      const Rat = ( y: number ) => tw * 0.84 * ( 1 - ( y - y0 ) / ( y1 - y0 ) );
      const ring = ( y: number ): [ number, number ][] => {
        const R = Rat( y );
        return [ P( x - 0.924 * R, y, zc - 0.383 * R ), P( x - 0.383 * R, y, zc - 0.924 * R ), P( x + 0.383 * R, y, zc - 0.924 * R ), P( x + 0.924 * R, y, zc - 0.383 * R ) ];
      };
      const band = ( ya: number, yb: number, col: RGB ) => {
        const A = ring( ya ), B = ring( yb );
        poly( [ A[ 0 ], A[ 1 ], B[ 1 ], B[ 0 ] ], this.rgba( litFace( col, 0.8, z ), 1 ) );
        poly( [ A[ 1 ], A[ 2 ], B[ 2 ], B[ 1 ] ], this.rgba( litFace( col, 0.05, z ), 1 ) );
        poly( [ A[ 2 ], A[ 3 ], B[ 3 ], B[ 2 ] ], this.rgba( litFace( col, -0.8, z ), 1 ) );
      };
      const yL = y0 + ( y1 - y0 ) * 0.34;
      band( y0, y0 + 4, copper );
      band( y0 + 4, yL - 1, this.mix( dark, copper, 0.22 ) );
      // the lantern ring, a band of green with its little openings
      {
        const A = ring( yL - 1 ), B = ring( yL + 3 );
        const grow = ( pts: [ number, number ][], k2: number ) => pts.map( ( [ px, py ] ) => [ ( px - this.P( x, 0, zc )[ 0 ] ) * k2 + this.P( x, 0, zc )[ 0 ], py ] as [ number, number ] );
        const Aw = grow( A, 1.12 ), Bw = grow( B, 1.12 );
        poly( [ Aw[ 0 ], Aw[ 1 ], Bw[ 1 ], Bw[ 0 ] ], this.rgba( litFace( copper, 0.8, z ), 1 ) );
        poly( [ Aw[ 1 ], Aw[ 2 ], Bw[ 2 ], Bw[ 1 ] ], this.rgba( litFace( copper, 0.05, z ), 1 ) );
        poly( [ Aw[ 2 ], Aw[ 3 ], Bw[ 3 ], Bw[ 2 ] ], this.rgba( litFace( copper, -0.8, z ), 1 ) );
        const c = P( x, yL + 1, zc - Rat( yL ) );
        g.fillStyle = this.rgba( inAir( [ 30, 32, 36 ], z ), 1 );
        g.fillRect( c[ 0 ] - s * 0.5, c[ 1 ] - s * 1.2, s * 1, s * 2 );
      }
      band( yL + 3, y1 - 0.01, this.mix( dark, copper, 0.12 ) );
      // its edges catching the light, green copper ribs from the foot to the top
      g.lineWidth = Math.max( 0.5, s * 0.22 );
      for ( let vi = 0; vi < 4; vi++ ) {
        const a = ring( y0 )[ vi ], b = ring( y1 - 0.01 )[ vi ];
        g.strokeStyle = this.rgba( litFace( copper, vi < 2 ? 0.8 : -0.2, z ), vi === 0 || vi === 3 ? 0.5 : 0.75 );
        g.beginPath(); g.moveTo( a[ 0 ], a[ 1 ] ); g.lineTo( b[ 0 ], b[ 1 ] ); g.stroke();
      }
      // the front turrets, over the spire's foot
      turret( x - tw + 0.8, z + 0.8 ); turret( x + tw - 0.8, z + 0.8 );
      // the cross
      const top = P( x, y1, zc ), cr = P( x, y1 + 6, zc ), arm = P( x, y1 + 4.2, zc );
      g.strokeStyle = this.rgba( inAir( dark, z ), 1 );
      g.lineWidth = Math.max( 0.6, s * 0.3 );
      g.beginPath(); g.moveTo( top[ 0 ], top[ 1 ] ); g.lineTo( cr[ 0 ], cr[ 1 ] ); g.moveTo( arm[ 0 ] - s * 1.2, arm[ 1 ] ); g.lineTo( arm[ 0 ] + s * 1.2, arm[ 1 ] ); g.stroke();
    } );
    // the cathedral's tower on Toompea, before St Olaf's: its dark baroque bell stages and lantern
    add( dome.z, () => {
      const { x, z } = dome;
      house( x - 6, x + 6, z + 8, 26, 14, 9, [ 226, 218, 196 ], tile, false, 0, 12 );
      const tw = 4.2, th = 26, s = F / z;
      const st: RGB = [ 236, 226, 200 ];
      poly( [ P( x - tw, 0, z ), P( x + tw, 0, z ), P( x + tw, th, z ), P( x - tw, th, z ) ], this.rgba( litFace( st, 0.5, z ), 1 ) );
      for ( const hy of [ 12, 20 ] ) {
        const [ wx, wy ] = P( x, hy, z );
        g.fillStyle = this.rgba( inAir( [ 80, 80, 96 ], z ), 1 );
        g.fillRect( wx - s * 0.7, wy - s * 3, s * 1.4, s * 3 );
      }
      const dark = inAir( [ 40, 44, 50 ], z ), rim = inAir( [ 112, 116, 120 ], z );
      const bulb = ( y0: number, y1: number, w0: number, wMax: number, w1: number ) => {
        const a = P( x - w0, y0, z ), b = P( x + w0, y0, z );
        const m1 = P( x - wMax, ( y0 * 0.55 + y1 * 0.45 ), z ), m2 = P( x + wMax, ( y0 * 0.55 + y1 * 0.45 ), z );
        const c1 = P( x - w1, y1, z ), c2 = P( x + w1, y1, z );
        const gr = g.createLinearGradient( a[ 0 ], 0, b[ 0 ], 0 );
        gr.addColorStop( 0, this.rgba( rim, 1 ) );
        gr.addColorStop( 0.4, this.rgba( dark, 1 ) );
        gr.addColorStop( 1, this.rgba( this.mix( dark, [ 20, 20, 26 ], 0.3 ), 1 ) );
        g.fillStyle = gr;
        g.beginPath();
        g.moveTo( a[ 0 ], a[ 1 ] );
        g.quadraticCurveTo( m1[ 0 ], m1[ 1 ], c1[ 0 ], c1[ 1 ] );
        g.lineTo( c2[ 0 ], c2[ 1 ] );
        g.quadraticCurveTo( m2[ 0 ], m2[ 1 ], b[ 0 ], b[ 1 ] );
        g.closePath();
        g.fill();
      };
      bulb( th, th + 7, tw + 0.6, tw + 1.4, 1.6 );
      const la = P( x - 1.8, th + 7, z ), lb = P( x + 1.8, th + 12, z );
      g.fillStyle = this.rgba( litFace( st, 0.4, z ), 1 );
      g.fillRect( la[ 0 ], lb[ 1 ], lb[ 0 ] - la[ 0 ], la[ 1 ] - lb[ 1 ] );
      g.fillStyle = this.rgba( inAir( [ 70, 72, 90 ], z ), 1 );
      g.fillRect( ( la[ 0 ] + lb[ 0 ] ) / 2 - s * 0.4, lb[ 1 ] + s * 1.2, s * 0.8, s * 2.4 );
      bulb( th + 12, th + 17, 2.4, 2.9, 0.4 );
      const sp0 = P( x, th + 17, z ), sp1 = P( x, th + 25, z );
      g.strokeStyle = this.rgba( dark, 1 );
      g.lineWidth = Math.max( 0.6, s * 0.4 );
      g.beginPath(); g.moveTo( sp0[ 0 ], sp0[ 1 ] ); g.lineTo( sp1[ 0 ], sp1[ 1 ] ); g.stroke();
    } );
    // the far towers on the left 80 px further left (owner: 30, then 50 more), their mirrors as far
    // right; the big one at the front stays, on its own layer among the swaying trees
    for ( const [ x0, z, rad, h, ch ] of towers ) {
      // (owner, later: the left ones 50 px further still)
      const x = z > 300 ? x0 + Math.sign( x0 ) * ( x0 < 0 ? 130 : 140 ) * z / F : x0;
      if ( z < SWAY_Z ) {
        add( z, () => {
          const fl = canvas();
          const keep = g;
          g = fl.g;
          tower( x, z, rad, h, ch );
          g = keep;
          this.front = { c: fl.c, z };
        } );
        continue;
      }
      add( z, () => tower( x, z, rad, h, ch ) );
    }
    // the snowy trees at our feet and along the slopes on both sides
    {
      const rr = this.seededLocal( 83 );
      for ( let z = 330; z > 132; z *= 0.97 ) {
        for ( const side of [ -1, 1 ] ) {
          for ( let x = 36; x < halfAt( z ); x += 8 + rr() * 9 ) {
            const zz = z * ( 1 + ( rr() - 0.5 ) * 0.02 ), xx = side * x;
            if ( !isClear( xx - 3, xx + 3, zz, zz + 3 ) ) { continue; }
            const h = 18 + rr() * 8, ts = Math.floor( rr() * 6 );
            add( zz, () => tree( xx, zz, h, ts ) );
          }
        }
      }
      // the nearest crowns, cut by the stage's foot so only their snowy tops show (owner)
      // (owner: one more row at the very front)
      for ( let z = 122; z > 88; z *= 0.95 ) {
        for ( let x = -halfAt( z ) - 6; x < halfAt( z ) + 6; x += 6 + rr() * 6 ) {
          const zz = z * ( 1 + ( rr() - 0.5 ) * 0.02 ), xx = x;
          const h = 20 + rr() * 7, ts = Math.floor( rr() * 6 );
          add( zz, () => tree( xx, zz, h, ts ) );
        }
      }
      // the middle of the foot too, and a band before the old town (owner: trees in front of the city)
      for ( let z = 400; z > 128; z *= 0.97 ) {
        for ( let x = -36; x < 36; x += 8 + rr() * 9 ) {
          const zz = z * ( 1 + ( rr() - 0.5 ) * 0.02 ), xx = x;
          if ( !isClear( xx - 3, xx + 3, zz, zz + 3 ) ) { continue; }
          const h = 18 + rr() * 8, ts = Math.floor( rr() * 6 );
          add( zz, () => tree( xx, zz, h, ts ) );
        }
      }
    }

    things.sort( ( a, b ) => b.z - a.z );
    for ( const t of things ) { t.draw(); }
    this.scene = sc.c;
  }

  private seededLocal( seed: number ): () => number {
    let s = ( seed * 2654435761 ) >>> 0;
    return () => {
      s = ( s + 0x6D2B79F5 ) >>> 0;
      let t = Math.imul( s ^ ( s >>> 15 ), 1 | s );
      t = ( t + Math.imul( t ^ ( t >>> 7 ), 61 | t ) ) ^ t;
      return ( ( t ^ ( t >>> 14 ) ) >>> 0 ) / 4294967296;
    };
  }

  /** The colour circle's five, or Tallinn's own: ice, harbour blue, indigo, brick red, a deeper red. */
  private five(): RGB[] {
    if ( this.paletteColors.length >= 5 ) { return this.paletteColors.slice( 0, 5 ); }
    return [ [ 208, 220, 239 ], [ 55, 124, 193 ], [ 66, 94, 184 ], [ 182, 86, 75 ], [ 177, 84, 75 ] ];
  }
}
